import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context';
import api from '../api/client';
import '../styles/profile.css';

export default function ProfilePage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('account');

  useEffect(() => {
    if (!user) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    refreshUser();
  }, []); // eslint-disable-line

  if (!user) return null;

  return (
    <div className="prof-wrap">
      <div className="prof-header">
        <div className="prof-avatar">
          {(user.nickname || user.name || '?').charAt(0).toUpperCase()}
        </div>
        <div className="prof-header-info">
          <h1>{user.nickname || user.name}</h1>
          <p>{user.email} · {user.role}</p>
        </div>
      </div>

      <div className="prof-tabs">
        {[
          { key: 'account',  label: 'Paskyra' },
          { key: 'security', label: 'Saugumas' },
        ].map(t => (
          <button key={t.key} className={`prof-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'account'  && <AccountTab user={user} onSaved={refreshUser} />}
      {tab === 'security' && <SecurityTab user={user} onSaved={refreshUser} logout={logout} navigate={navigate} />}
    </div>
  );
}

// ── Account tab ────────────────────────────────────────────────────────────────

function AccountTab({ user, onSaved }) {
  const [form, setForm] = useState({
    name:     user.name     || '',
    nickname: user.nickname || '',
    address:  user.address  || '',
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');

  useEffect(() => {
    setForm({ name: user.name || '', nickname: user.nickname || '', address: user.address || '' });
  }, [user]);

  const save = useCallback(async () => {
    setSaving(true); setSuccess(''); setError('');
    try {
      await api.put('/auth/me', form);
      await onSaved();
      setSuccess('Išsaugota sėkmingai!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Klaida išsaugant');
    } finally {
      setSaving(false);
    }
  }, [form, onSaved]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <div className="prof-card">
        <div className="prof-card-title">Asmeninė informacija</div>

        {success && <div className="prof-success">✓ {success}</div>}
        {error   && <div className="prof-error">⚠ {error}</div>}

        <div className="prof-grid">
          <div className="prof-field">
            <label>Vardas ir Pavardė</label>
            <input value={form.name} onChange={set('name')} placeholder="Vardas Pavardė" />
          </div>
          <div className="prof-field">
            <label>Slapyvardis (NickName)</label>
            <input value={form.nickname} onChange={set('nickname')} placeholder="slapyvardis" />
          </div>
          <div className="prof-field full">
            <label>Adresas</label>
            <input value={form.address} onChange={set('address')} placeholder="Gatvė, miestas" />
          </div>
          <div className="prof-field full">
            <label>El. paštas</label>
            <input value={user.email} disabled style={{ opacity: 0.45, cursor: 'not-allowed' }} />
          </div>
        </div>

        <div className="prof-footer">
          <button className="prof-btn prof-btn-primary" onClick={save} disabled={saving || !form.name.trim()}>
            {saving ? '⏳ Saugoma...' : '💾 Išsaugoti'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Security tab ───────────────────────────────────────────────────────────────

function SecurityTab({ user, onSaved, logout, navigate }) {
  const [mfa,     setMfa]     = useState(user.mfa_enabled ?? 1);
  const [mfaSaving, setMfaSaving] = useState(false);
  const [mfaMsg,  setMfaMsg]  = useState('');

  const [pw, setPw] = useState({ current: '', new: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError,   setPwError]   = useState('');

  const toggleMfa = useCallback(async () => {
    setMfaSaving(true); setMfaMsg('');
    const next = mfa ? 0 : 1;
    try {
      await api.put('/auth/mfa', { mfa_enabled: next });
      setMfa(next);
      setMfaMsg(next ? 'MFA įjungta' : 'MFA išjungta');
      await onSaved();
      setTimeout(() => setMfaMsg(''), 3000);
    } catch {
      setMfaMsg('Klaida keičiant MFA');
    } finally {
      setMfaSaving(false);
    }
  }, [mfa, onSaved]);

  const changePassword = useCallback(async () => {
    setPwSuccess(''); setPwError('');
    if (pw.new !== pw.confirm) { setPwError('Nauji slaptažodžiai nesutampa'); return; }
    if (pw.new.length < 8)     { setPwError('Slaptažodis turi būti bent 8 simbolių'); return; }
    setPwSaving(true);
    try {
      await api.put('/auth/password', { current_password: pw.current, new_password: pw.new });
      setPwSuccess('Slaptažodis pakeistas sėkmingai!');
      setPw({ current: '', new: '', confirm: '' });
      setTimeout(() => setPwSuccess(''), 4000);
    } catch (e) {
      setPwError(e?.response?.data?.detail || 'Klaida keičiant slaptažodį');
    } finally {
      setPwSaving(false);
    }
  }, [pw]);

  return (
    <>
      {/* MFA */}
      <div className="prof-card">
        <div className="prof-card-title">Dviejų žingsnių autentifikacija (MFA)</div>

        {mfaMsg && (
          <div className={mfa ? 'prof-success' : 'prof-error'} style={{ marginBottom: 14 }}>
            {mfaMsg}
          </div>
        )}

        <div className="prof-mfa-row">
          <div className="prof-mfa-info">
            <strong>El. pašto patvirtinimas</strong>
            <span>Prisijungus reikalingas OTP kodas el. paštu</span>
          </div>
          <button
            className="prof-toggle"
            style={{ background: mfa ? 'rgba(64,200,100,0.7)' : 'rgba(255,255,255,0.15)' }}
            onClick={toggleMfa}
            disabled={mfaSaving}
            title={mfa ? 'Išjungti MFA' : 'Įjungti MFA'}
          >
            <div className="prof-toggle-knob" style={{ left: mfa ? 22 : 2 }} />
          </button>
        </div>

        <p style={{ fontSize: 11, color: 'rgba(210,195,250,0.35)', marginTop: 12 }}>
          {mfa
            ? '🔒 MFA įjungta – kiekvienas prisijungimas reikalauja el. pašto patvirtinimo'
            : '⚠️ MFA išjungta – rekomenduojama įjungti dėl saugumo'}
        </p>
      </div>

      {/* Password change */}
      <div className="prof-card">
        <div className="prof-card-title">Slaptažodžio keitimas</div>

        {pwSuccess && <div className="prof-success">✓ {pwSuccess}</div>}
        {pwError   && <div className="prof-error">⚠ {pwError}</div>}

        <div className="prof-grid">
          <div className="prof-field full">
            <label>Dabartinis slaptažodis</label>
            <input type="password" value={pw.current} onChange={e => setPw(f => ({ ...f, current: e.target.value }))} placeholder="••••••••" />
          </div>
          <div className="prof-field">
            <label>Naujas slaptažodis</label>
            <input type="password" value={pw.new} onChange={e => setPw(f => ({ ...f, new: e.target.value }))} placeholder="••••••••" />
          </div>
          <div className="prof-field">
            <label>Pakartoti naują</label>
            <input type="password" value={pw.confirm} onChange={e => setPw(f => ({ ...f, confirm: e.target.value }))} placeholder="••••••••" />
          </div>
        </div>

        <div className="prof-footer">
          <button
            className="prof-btn prof-btn-primary"
            onClick={changePassword}
            disabled={pwSaving || !pw.current || !pw.new || !pw.confirm}
          >
            {pwSaving ? '⏳ Keičiama...' : '🔑 Pakeisti slaptažodį'}
          </button>
        </div>
      </div>

      {/* Danger zone */}
      <div className="prof-card" style={{ borderColor: 'rgba(240,60,60,0.15)' }}>
        <div className="prof-card-title" style={{ color: 'rgba(255,100,100,0.5)' }}>Kita</div>
        <button
          className="prof-btn"
          style={{ background: 'rgba(240,60,60,0.1)', border: '1px solid rgba(240,60,60,0.25)', color: '#ff7070' }}
          onClick={() => { logout(); navigate('/'); }}
        >
          🚪 Atsijungti
        </button>
      </div>
    </>
  );
}
